// La scheda di una pratica reso/sostituzione (redesign 27/10/2026, Davide:
// «troppo povera di informazioni, rifalla completamente»). Tre colonne che
// rispondono alle tre domande dell'operatore: di quale ordine si parla (canale,
// cliente, prodotti con la foto), da dove a dove va il pacco, e con cosa
// (corriere, tracking, colli). Le azioni stanno in fondo, sempre nello stesso
// posto.
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { TrackingPanel } from "@/features/messaggi/components/TrackingPanel";
import { orderMarketplaceLogo } from "@/features/ordini/components/OrderCard";
import {
  coloriCorriere,
  coloriPaese,
  contorno,
  FORMA_BADGE,
  pieno,
} from "@/features/ordini/components/statoToni";
import {
  oggiIso,
  ritiroResoMancabile,
  STATI_ATTIVI,
  type Prenotazione,
} from "@/features/ritiri/api";
import { apiAssetUrl } from "@/shared/api";
import { openExternalUrl } from "@/shared/openExternalUrl";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import type { AddressForm, OrdineCollegato, ReturnRecord } from "../api";
import { KindBadge, StatusBadge } from "./PraticaBadge";

const SLOT: Record<string, string> = {
  cliente: "Cliente",
  bscom: "BS COM",
  itamoby: "Itamoby",
  ityhome: "Ityhome",
  cosma: "Cosma",
  bizzotto: "Bizzotto",
  allservice: "AllService",
  webfurniture: "Web Furniture",
};

/** Oltre questi giorni una pratica ancora aperta si segnala: un reso fermo
 *  e' un rimborso o una sostituzione che il cliente sta aspettando. */
const GIORNI_FERMA = 10;
const APERTE = new Set(["richiesta", "etichetta_pronta", "in_transito"]);

const data = (iso: string) => new Date(iso).toLocaleDateString("it-IT");
const euro = (importo: string, valuta: string) =>
  Number(importo).toLocaleString("it-IT", { style: "currency", currency: valuta || "EUR" });
const kg = (n: number) => n.toLocaleString("it-IT", { maximumFractionDigits: 1 });

function giorniDa(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

const TASTO =
  "inline-flex h-[32px] items-center gap-[6px] rounded-full border px-4 text-[12.5px] font-semibold transition-[filter,border-color] duration-[120ms] focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/50 disabled:opacity-50 max-md:h-10 max-md:text-[14px]";
const TASTO_NERO = `${TASTO} border-ink bg-ink text-on-ink hover:brightness-125`;
const TASTO_CHIARO = `${TASTO} border-line-2 bg-card text-ink hover:border-ink-4`;

export function PraticaCard({
  record,
  ordine,
  onOpenLabel,
  onEmettiEtichetta,
  ritiro,
  onPrenotaRitiro,
}: {
  record: ReturnRecord;
  /** L'ordine collegato; ``undefined`` = la reference non e' un ordine in OM. */
  ordine?: OrdineCollegato;
  /** Risolve a download/apertura conclusi (errori gia' gestiti dal genitore). */
  onOpenLabel: () => Promise<void>;
  /** Apre il passo 2 (colli + emissione) per una pratica ancora in richiesta. */
  onEmettiEtichetta: () => void;
  /** L'ultima prenotazione del ritiro dal cliente, in qualunque stato.
   *  ``undefined`` = mappa non ancora caricata: niente chip, «mai chiesto»
   *  non e' un'affermazione da fare prima di aver controllato davvero. */
  ritiro?: Prenotazione | null;
  /** Apre il dialog del ritiro; assente = niente permesso dell'area spedizioni. */
  onPrenotaRitiro?: () => void;
}) {
  // Il TrackingPanel si monta SOLO all'espansione: una lista piena non lancia
  // una fetch tracking per ogni riga.
  const [trackingOpen, setTrackingOpen] = useState(false);
  // Sul web il PDF passa da fetch + blob: senza feedback il click sembra non
  // passato e si scarica due volte.
  const [labelBusy, setLabelBusy] = useState(false);

  async function handleLabel(): Promise<void> {
    if (labelBusy) return;
    setLabelBusy(true);
    try {
      await onOpenLabel();
    } finally {
      setLabelBusy(false);
    }
  }

  const giorni = giorniDa(record.created_at);
  const ferma = APERTE.has(record.status) && giorni >= GIORNI_FERMA;
  const hasTracking = record.tracking_numbers.length > 0;

  return (
    <article
      aria-label={`Pratica ${record.order_reference}`}
      className="overflow-hidden rounded-2xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] transition-colors duration-[120ms] hover:border-ink-4"
    >
      <header className="flex flex-wrap items-center gap-[7px] border-b border-line px-4 py-[11px]">
        <KindBadge kind={record.kind} />
        <StatusBadge status={record.status} />
        {record.kind === "reso" && record.status === "etichetta_pronta" && (
          <ChipRitiro ritiro={ritiro} onPrenota={onPrenotaRitiro} />
        )}
        <span className="ml-auto flex items-center gap-2 text-[12.5px] tabular-nums text-ink-2 max-md:ml-0 max-md:w-full">
          {record.num_rif_interno && (
            <span className="font-semibold text-ink" title="Rif. interno sull'etichetta">
              {record.num_rif_interno}
            </span>
          )}
          <span>aperta il {data(record.created_at)}</span>
          {ferma ? (
            <span className={FORMA_BADGE} style={contorno("scaduta")}>
              ferma da {giorni} giorni
            </span>
          ) : (
            APERTE.has(record.status) && <span>· {giorni === 0 ? "oggi" : `${giorni} gg fa`}</span>
          )}
        </span>
      </header>

      <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1fr)] max-lg:grid-cols-2 max-md:grid-cols-1">
        <Sezione titolo="Ordine" className="max-lg:col-span-2 max-md:col-span-1">
          <BloccoOrdine record={record} ordine={ordine} />
        </Sezione>
        <Sezione titolo="Percorso del pacco" className="border-line lg:border-l max-lg:border-t">
          <Tappa etichetta="Parte da" slot={record.sender_slot} indirizzo={record.sender} primo />
          <Tappa etichetta="Arriva a" slot={record.recipient_slot} indirizzo={record.recipient} />
        </Sezione>
        <Sezione
          titolo="Spedizione"
          className="border-l border-line max-lg:border-t max-md:border-l-0"
        >
          <BloccoSpedizione record={record} />
        </Sezione>
      </div>

      <footer className="flex flex-wrap items-center gap-[7px] border-t border-line bg-paper/60 px-4 py-[10px]">
        {/* In «richiesta» l'etichetta non esiste ancora: il download darebbe
            un 404 su una pratica sana. */}
        {record.status === "richiesta" ? (
          <button
            type="button"
            onClick={onEmettiEtichetta}
            className={`${TASTO} border-violet bg-violet text-white hover:bg-violet-2`}
          >
            Emetti etichetta
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void handleLabel()}
            disabled={labelBusy}
            aria-busy={labelBusy}
            className={TASTO_NERO}
          >
            {labelBusy ? "Apro…" : "Etichetta"}
          </button>
        )}
        {hasTracking && (
          <button
            type="button"
            onClick={() => setTrackingOpen((v) => !v)}
            aria-expanded={trackingOpen}
            className={TASTO_CHIARO}
          >
            <IconChevron open={trackingOpen} />
            Tracking
          </button>
        )}
        {ordine && (
          <Link
            to={`/ordini/${encodeURIComponent(ordine.reference)}`}
            className={`${TASTO_CHIARO} ml-auto max-md:ml-0`}
          >
            Apri ordine
          </Link>
        )}
      </footer>
      {trackingOpen && (
        <div className="border-t border-line bg-paper p-3">
          <TrackingPanel
            carrier={record.carrier ?? "SDA"}
            trackingCode={record.tracking_numbers[0] ?? ""}
            parcelCodes={record.tracking_numbers}
            onOpenExternal={openExternalUrl}
          />
        </div>
      )}
    </article>
  );
}

function Sezione({
  titolo,
  className = "",
  children,
}: {
  titolo: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`min-w-0 px-4 py-3 ${className}`}>
      <h3 className="mb-2 text-[12px] font-semibold text-ink-2">{titolo}</h3>
      {children}
    </section>
  );
}

function BloccoOrdine({ record, ordine }: { record: ReturnRecord; ordine?: OrdineCollegato }) {
  // Gli SKU che viaggiano nel pacco: sull'ordine si segnano, cosi' su un ordine
  // di piu' prodotti si vede quale torna indietro.
  const nelPacco = new Set(record.items.map((i) => i.sku.trim()).filter(Boolean));
  if (!ordine) {
    return (
      <>
        <div className="font-display text-[15px] font-bold text-ink">{record.order_reference}</div>
        <p className="mt-0.5 text-[12.5px] text-ink-2">Ordine non trovato in OM</p>
        {record.items.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1.5">
            {record.items.map((it, i) => (
              <li key={`${it.sku}-${i}`} className="text-[13px] text-ink">
                <span className="font-semibold tabular-nums">{it.sku || "—"}</span>
                <span className="text-ink-2"> × {it.quantita}</span>
                {it.descrizione && <span className="text-ink-2"> · {it.descrizione}</span>}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  const logo = orderMarketplaceLogo({
    raw: { source: ordine.channel || ordine.external_source },
    marketplace: ordine.channel,
  });
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <MarketplaceLogo {...logo} size={16} showLabel />
        <Link
          to={`/ordini/${encodeURIComponent(ordine.reference)}`}
          className="font-display text-[15px] font-bold tabular-nums text-ink hover:underline"
        >
          {ordine.reference}
        </Link>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 text-[12.5px] text-ink-2">
        <span className="font-semibold text-ink">
          {ordine.customer_name || "Cliente senza nome"}
        </span>
        {ordine.created_at_poleepo && (
          <span className="tabular-nums">· ordinato il {data(ordine.created_at_poleepo)}</span>
        )}
        <span className="tabular-nums">· {euro(ordine.total_amount, ordine.currency)}</span>
      </div>
      <ul className="mt-2.5 flex flex-col gap-2">
        {ordine.items.map((it) => (
          <li key={it.id} className="flex items-center gap-2.5">
            <Foto id={it.id} url={it.image_url} titolo={it.title} />
            <div className="min-w-0">
              <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-ink">
                {it.title || it.sku}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] tabular-nums text-ink-2">
                <span>{it.sku}</span>
                <span>× {it.quantity}</span>
                {nelPacco.has(it.sku.trim()) && (
                  <span className={FORMA_BADGE} style={contorno("sost-as")}>
                    nel pacco
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function Foto({ id, url, titolo }: { id: number; url: string; titolo: string }) {
  const [rotta, setRotta] = useState(false);
  const cornice =
    "h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-line bg-paper-2 max-md:h-14 max-md:w-14";
  if (!url || rotta)
    return (
      <span aria-hidden className={`${cornice} grid place-items-center text-ink-2`}>
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 fill-none stroke-current stroke-[1.5] [stroke-linecap:round] [stroke-linejoin:round]"
        >
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M21 16l-5-5-9 9" />
        </svg>
      </span>
    );
  return (
    <span className={cornice}>
      <img
        src={apiAssetUrl(`/api/orders/items/${id}/thumb`)}
        alt={titolo}
        loading="lazy"
        onError={() => setRotta(true)}
        className="h-full w-full object-cover"
      />
    </span>
  );
}

function Tappa({
  etichetta,
  slot,
  indirizzo,
  primo,
}: {
  etichetta: string;
  slot: string;
  indirizzo: Partial<AddressForm>;
  primo?: boolean;
}) {
  const nome = [indirizzo.name, indirizzo.surname].filter(Boolean).join(" ");
  const paese = (indirizzo.country_iso || "IT").toUpperCase();
  return (
    <div className={`relative pl-5 ${primo ? "pb-3" : ""}`}>
      {/* Il filo del percorso: dal primo pallino al secondo. */}
      {primo && (
        <span aria-hidden className="absolute top-[9px] bottom-[-6px] left-[4px] w-px bg-line-2" />
      )}
      <span
        aria-hidden
        className={`absolute top-[5px] left-0 h-[9px] w-[9px] rounded-full border-2 ${primo ? "border-ink-2 bg-card" : "border-ink bg-ink"}`}
      />
      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-ink-2">
        {etichetta}
        {SLOT[slot] && slot !== "cliente" && (
          <span className={FORMA_BADGE} style={contorno("annullato")}>
            {SLOT[slot]}
          </span>
        )}
      </div>
      <div className="truncate text-[13.5px] font-semibold text-ink">
        {indirizzo.company || nome || "—"}
      </div>
      {nome && indirizzo.company && nome !== indirizzo.company && (
        <div className="truncate text-[12.5px] text-ink">{nome}</div>
      )}
      <div className="text-[12.5px] leading-snug text-ink-2">
        {indirizzo.address && <div className="truncate">{indirizzo.address}</div>}
        <div className="flex flex-wrap items-center gap-1.5 tabular-nums">
          {[indirizzo.postcode, indirizzo.city].filter(Boolean).join(" ")}
          {indirizzo.state_iso && ` (${indirizzo.state_iso})`}
          {paese !== "IT" && (
            <span className={FORMA_BADGE} style={coloriPaese(paese)}>
              {paese}
            </span>
          )}
        </div>
        {indirizzo.phone && (
          <a href={`tel:${indirizzo.phone}`} className="tabular-nums text-ink hover:underline">
            {indirizzo.phone}
          </a>
        )}
      </div>
    </div>
  );
}

function BloccoSpedizione({ record }: { record: ReturnRecord }) {
  const corriere = record.carrier ?? "SDA";
  const colori = coloriCorriere(corriere);
  const pesoTot = record.packages.reduce((s, p) => s + (p.kg || 0), 0);
  const m3 = record.packages.reduce((s, p) => s + (p.a * p.l * p.p) / 1_000_000, 0);
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={FORMA_BADGE} style={colori ?? contorno("annullato")} title="Corriere">
          {corriere}
        </span>
        {record.tracking_numbers.length === 0 ? (
          <span className="text-[12.5px] text-ink-2">nessun tracking</span>
        ) : (
          record.tracking_numbers.map((t) => (
            <span key={t} className="select-all text-[12.5px] font-semibold tabular-nums text-ink">
              {t}
            </span>
          ))
        )}
      </div>
      {record.packages.length > 0 ? (
        <>
          <div className="mt-2.5 text-[13px] font-semibold tabular-nums text-ink">
            {record.packages.length} {record.packages.length === 1 ? "collo" : "colli"} ·{" "}
            {kg(pesoTot)} kg
            {m3 > 0 && (
              <span className="font-normal text-ink-2">
                {" "}
                · {m3.toLocaleString("it-IT", { maximumFractionDigits: 2 })} m³
              </span>
            )}
          </div>
          <ol className="mt-1 flex flex-col gap-0.5 text-[12.5px] tabular-nums text-ink-2">
            {record.packages.map((p, i) => (
              <li key={i}>
                {i + 1}. {p.l}×{p.p}×{p.a} cm · {kg(p.kg)} kg
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="mt-2.5 text-[12.5px] text-ink-2">Colli non ancora indicati</p>
      )}
      {record.contenuto && (
        <p className="mt-2 text-[12.5px] text-ink">
          <span className="text-ink-2">Contenuto: </span>
          {record.contenuto}
        </p>
      )}
      {record.note && (
        <p className="mt-2 rounded-lg bg-paper-2 px-2.5 py-1.5 text-[12.5px] leading-snug text-ink">
          {record.note}
        </p>
      )}
    </>
  );
}

function IconChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-[13px] w-[13px] shrink-0 fill-none stroke-current stroke-[1.7] transition-transform [stroke-linecap:round] [stroke-linejoin:round] ${
        open ? "rotate-90" : ""
      }`}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/** Il ritiro dal cliente di un reso: prenotato, da rifare (rifiutato o annullato:
 *  «Ritiro da prenotare», spec §5) o mai chiesto (link neutro). Senza
 *  ``onPrenota`` resta solo l'informazione. ``undefined`` = mappa non ancora
 *  caricata: niente chip, nemmeno il link neutro. */
function ChipRitiro({
  ritiro,
  onPrenota,
}: {
  ritiro: Prenotazione | null | undefined;
  onPrenota?: () => void;
}) {
  if (ritiro === undefined) return null;
  if (ritiro && STATI_ATTIVI.includes(ritiro.stato)) {
    const giorno = new Date(`${ritiro.data_ritiro}T12:00:00`).toLocaleDateString("it-IT", {
      weekday: "short",
      day: "numeric",
      month: "numeric",
    });
    const chip = (
      <span className={FORMA_BADGE} style={contorno("pronto-ritiro")}>
        Ritiro {giorno} · {ritiro.codice ?? "da verificare"}
      </span>
    );
    // Giorno arrivato: se il corriere non e' passato si riprenota sulla stessa LDV.
    if (!onPrenota || !ritiroResoMancabile(ritiro, oggiIso())) return chip;
    return (
      <>
        {chip}
        <button
          type="button"
          onClick={onPrenota}
          className={`${FORMA_BADGE} hover:brightness-110`}
          style={pieno("ldv-mancante")}
        >
          Non è passato? Riprenota
        </button>
      </>
    );
  }
  if (!onPrenota) return null;
  return ritiro ? (
    <button
      type="button"
      onClick={onPrenota}
      className={`${FORMA_BADGE} hover:brightness-110`}
      style={pieno("ldv-mancante")}
    >
      Ritiro da prenotare
    </button>
  ) : (
    <button
      type="button"
      onClick={onPrenota}
      className={`${FORMA_BADGE} hover:brightness-110`}
      style={contorno("pronto-ritiro")}
    >
      Prenota ritiro
    </button>
  );
}
